import { queryOptions, skipToken, useQuery } from '@tanstack/react-query'
import type {
  PbFindItemReturnReason,
  PbReturnReasonsCollection
} from '@itsrever/rever-api'
import { settingsApi } from '@/redux/api/settings-api-slice'
import { useAppSelector } from '@/redux/hooks'
import { shuffled } from '@/utils/shuffle'

export const getItemReturnReasonsQuery = (
  { ecommerce_id, category_ids }: PbFindItemReturnReason,
  randomizeOrder = false
) =>
  queryOptions({
    queryKey: ['return-reasons', ecommerce_id, category_ids],
    queryFn:
      ecommerce_id !== undefined
        ? async () => {
            const { data } = await settingsApi.findItemReturnReasonsByBody({
              pbFindItemReturnReason: { ecommerce_id, category_ids }
            })

            return randomizeOrder ? randomizedReasons(data) : data
          }
        : skipToken,
    retry: false
  })

// Shuffled once per fetch, so each shopper sees a random order that stays
// stable while the query is cached instead of reshuffling on every render.
export function randomizedReasons(
  data: PbReturnReasonsCollection
): PbReturnReasonsCollection {
  return {
    ...data,
    return_reasons: shuffled(data.return_reasons ?? []).map((reason) => ({
      ...reason,
      available_reasons: reason.available_reasons
        ? shuffled(reason.available_reasons)
        : reason.available_reasons
    }))
  }
}

export const useItemReturnReasons = (request: PbFindItemReturnReason) => {
  const randomizeOrder = useAppSelector(
    (store) =>
      store.settingsApi.settings.response.randomize_return_reasons_order
  )
  return useQuery(getItemReturnReasonsQuery(request, randomizeOrder ?? false))
}
